//! Dragging page breaks and the print-area boundary in Page Break Preview.
//!
//! Excel's semantics: dragging an automatic break makes it manual, dragging a
//! break off the end of the data removes it, dragging the boundary resizes the
//! print area, and dragging a break outward past what fits switches scaling to
//! "Adjust to N%" instead of ignoring the drag.

import { sheets } from '@/app/grid/controller/Sheets';
import type { PrintAreaEdge } from '@/app/gridGL/UI/pageBreaks/pageBreakEdits';
import {
  applyBreakDrag,
  requiredScalePercent,
  resizePrintArea,
  snapToTrack,
} from '@/app/gridGL/UI/pageBreaks/pageBreakEdits';
import { pageBreaks } from '@/app/gridGL/UI/pageBreaks/pageBreaks';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import {
  buildSheetPrintSetup,
  columnNumberToLetter,
  defaultPageSetupState,
  type SheetPrintSetupPayload,
} from '@/app/ui/menus/TopBar/PageSetup/pageSetupState';
import type { FederatedPointerEvent, Point } from 'pixi.js';

/** Grab distance from a line, in screen pixels. */
const HIT_TOLERANCE = 4;

type PrintAreaDragType = 'print-left' | 'print-right' | 'print-top' | 'print-bottom';
type DragType = 'col-break' | 'row-break' | PrintAreaDragType;

const PRINT_AREA_EDGES: Record<PrintAreaDragType, PrintAreaEdge> = {
  'print-left': 'left',
  'print-right': 'right',
  'print-top': 'top',
  'print-bottom': 'bottom',
};

interface Drag {
  type: DragType;
  /** Boundary the drag started from, as a 1-based track index. */
  from: number;
  /** First track of the page being resized (breaks only). */
  bandStart: number;
  /** Current snapped boundary. */
  target: number;
}

const isVertical = (type: DragType): boolean => type === 'col-break' || type === 'print-left' || type === 'print-right';

export class PointerPageBreak {
  private drag: Drag | undefined;
  cursor: string | undefined;

  get dragging(): boolean {
    return this.drag !== undefined;
  }

  pointerDown(world: Point, event: FederatedPointerEvent): boolean {
    if (event.button !== 0 || !pageBreaks.enabled) return false;
    const hit = this.hitTest(world);
    if (!hit) return false;

    this.drag = hit;
    this.cursor = isVertical(hit.type) ? 'col-resize' : 'row-resize';
    pageBreaks.preview = { vertical: isVertical(hit.type), boundary: hit.target };
    pixiApp.viewport.pauseDrag();
    return true;
  }

  pointerMove(world: Point): boolean {
    if (!this.drag) {
      const hit = this.hitTest(world);
      this.cursor = hit ? (isVertical(hit.type) ? 'col-resize' : 'row-resize') : undefined;
      return !!hit;
    }

    this.drag.target = this.snap(world, isVertical(this.drag.type));
    pageBreaks.preview = { vertical: isVertical(this.drag.type), boundary: this.drag.target };
    return true;
  }

  pointerUp(): boolean {
    if (!this.drag) return false;
    const drag = this.drag;
    this.reset();
    if (drag.target !== drag.from) {
      this.commit(drag);
    }
    return true;
  }

  handleEscape(): boolean {
    if (!this.drag) return false;
    this.reset();
    return true;
  }

  private reset() {
    this.drag = undefined;
    this.cursor = undefined;
    pageBreaks.preview = undefined;
    pixiApp.viewport.resumeDrag();
  }

  /** The boundary nearest the pointer, so a line never lands inside a track. */
  private snap(world: Point, vertical: boolean): number {
    const offsets = sheets.sheet.offsets;
    if (vertical) {
      const hovered = sheets.sheet.getColumnFromScreen(world.x);
      const placement = offsets.getColumnPlacement(hovered);
      return Math.max(
        1,
        snapToTrack({ position: world.x, trackStart: placement.position, trackSize: placement.size, hovered })
      );
    }
    const hovered = sheets.sheet.getRowFromScreen(world.y);
    const placement = offsets.getRowPlacement(hovered);
    return Math.max(
      1,
      snapToTrack({ position: world.y, trackStart: placement.position, trackSize: placement.size, hovered })
    );
  }

  private hitTest(world: Point): Drag | undefined {
    const pagination = pageBreaks.current;
    const printRect = pageBreaks.getPrintRect();
    const printAreaRect = pageBreaks.getPrintAreaRect();
    if (!pagination || !printRect || !printAreaRect) return undefined;

    const tolerance = HIT_TOLERANCE / pixiApp.viewport.scale.x;
    const withinRows = world.y >= printRect.top - tolerance && world.y <= printRect.bottom + tolerance;
    const withinCols = world.x >= printRect.left - tolerance && world.x <= printRect.right + tolerance;

    if (withinRows) {
      for (const [index, line] of pageBreaks.getColumnBreaks().entries()) {
        if (Math.abs(world.x - line.position) <= tolerance) {
          return {
            type: 'col-break',
            from: line.index,
            bandStart: Number(pagination.col_bands[index].start),
            target: line.index,
          };
        }
      }
    }
    if (withinCols) {
      for (const [index, line] of pageBreaks.getRowBreaks().entries()) {
        if (Math.abs(world.y - line.position) <= tolerance) {
          return {
            type: 'row-break',
            from: line.index,
            bandStart: Number(pagination.row_bands[index].start),
            target: line.index,
          };
        }
      }
    }

    // The print area's own border, which resizes it. Its edges are hit-tested
    // against the area rather than the printed region: print titles outside the
    // area do print, so they are not shaded, but they are not part of the area
    // either — its left and top edges are the ones a drag moves.
    const withinAreaRows = world.y >= printAreaRect.top - tolerance && world.y <= printAreaRect.bottom + tolerance;
    const withinAreaCols = world.x >= printAreaRect.left - tolerance && world.x <= printAreaRect.right + tolerance;

    if (withinAreaRows && Math.abs(world.x - printAreaRect.left) <= tolerance) {
      return this.edgeDrag('print-left', Number(pagination.print_area_min_x));
    }
    if (withinAreaRows && Math.abs(world.x - printAreaRect.right) <= tolerance) {
      return this.edgeDrag('print-right', Number(pagination.max_x) + 1);
    }
    if (withinAreaCols && Math.abs(world.y - printAreaRect.top) <= tolerance) {
      return this.edgeDrag('print-top', Number(pagination.print_area_min_y));
    }
    if (withinAreaCols && Math.abs(world.y - printAreaRect.bottom) <= tolerance) {
      return this.edgeDrag('print-bottom', Number(pagination.max_y) + 1);
    }
    return undefined;
  }

  private edgeDrag(type: DragType, boundary: number): Drag {
    return { type, from: boundary, bandStart: boundary, target: boundary };
  }

  private commit(drag: Drag) {
    const pagination = pageBreaks.current;
    if (!pagination) return;
    const sheet = sheets.sheet;
    const setup = currentPrintSetup();

    if (drag.type === 'col-break' || drag.type === 'row-break') {
      const vertical = drag.type === 'col-break';
      const regionEnd = Number(vertical ? pagination.max_x : pagination.max_y);
      const { breaks, bandEnd } = applyBreakDrag({
        breaks: vertical ? setup.colBreaks : setup.rowBreaks,
        from: drag.from,
        to: drag.target,
        bandStart: drag.bandStart,
        regionEnd,
      });

      const offsets = sheet.offsets;
      const bandPx = vertical
        ? offsets.getColumnPlacement(bandEnd).position - offsets.getColumnPlacement(drag.bandStart).position
        : offsets.getRowPlacement(bandEnd).position - offsets.getRowPlacement(drag.bandStart).position;
      const percent = requiredScalePercent({
        bandPx,
        titlePx: vertical ? pagination.title_cols_width : pagination.title_rows_height,
        contentPx: vertical ? pagination.content_width_px : pagination.content_height_px,
        currentScale: pagination.scale,
      });

      sheet.setPrintSetup(
        {
          ...setup,
          ...(vertical ? { colBreaks: breaks } : { rowBreaks: breaks }),
          ...(percent === undefined ? {} : { pageSetup: { ...setup.pageSetup, scaling: { type: 'zoom', percent } } }),
        },
        false
      );
      return;
    }

    sheet.setPrintSetup(
      { ...setup, printArea: this.resizedPrintArea(PRINT_AREA_EDGES[drag.type], drag.target, pagination) },
      false
    );
  }

  /**
   * The print area after dragging one of its edges, in A1 (`B2:H30`). The
   * area is taken from the pagination's own print-area bounds rather than the
   * shaded region, which also covers print titles outside it.
   */
  private resizedPrintArea(
    edge: PrintAreaEdge,
    target: number,
    pagination: NonNullable<typeof pageBreaks.current>
  ): string {
    const { minX, minY, maxX, maxY } = resizePrintArea(
      {
        minX: Number(pagination.print_area_min_x),
        minY: Number(pagination.print_area_min_y),
        maxX: Number(pagination.max_x),
        maxY: Number(pagination.max_y),
      },
      edge,
      target
    );
    return `${columnNumberToLetter(minX)}${minY}:${columnNumberToLetter(maxX)}${maxY}`;
  }
}

/** The sheet's persisted setup, or Excel's defaults when it has none yet. */
export const currentPrintSetup = (): SheetPrintSetupPayload =>
  sheets.sheet.printSetup ?? buildSheetPrintSetup(defaultPageSetupState(), { rowBreaks: [], colBreaks: [] });
